// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useReactToPrint } from 'react-to-print';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

import { css } from '@emotion/css';
import {
  Theme,
  useTheme,
  Typography,
  Button,
  CircularProgress,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Box,
} from '@mui/material';
import { BiPrinter, BiCopy, BiMessageRoundedDetail, BiQr } from 'react-icons/bi';

import Modal from '@components/ModalComponents/Modal';
import ModalHeader from '@components/ModalComponents/ModalHeader';
import ModalContent from '@components/ModalComponents/ModalContent';
import ModalFooter from '@components/ModalComponents/ModalFooter';
import { copyToClipboard } from '@services/utilityService';
import { useInsertGuestRideSmsMessageMutation } from '../queries/insertGuestRideSmsMessage.mutation.generated';
import { useGetSmsTemplateByTypeLazyQuery } from '../queries/getSmsTemplateByType.query.generated';
import { useUser } from '@store';
import config from '../../../config.frontend';

import { fetchGuestLink } from '../services/guestLinkService';

// TYPES ---------------------------------------------------------------- //

type ShareMode = 'qr' | 'sms';

interface GuestLinkModalProps {
  open: boolean;
  onClose: () => void;
  /** ID passed to `fetchGuestLink`. For rooftop-wide links this is the rooftop id; for per-ride links, the guestride id. */
  entityId?: number;
  /** Which id_type the Netlify function expects. */
  entityType?: 'customer' | 'guestride';
  /** SMS template `type` to fetch and render. */
  templateType: string;
  /** Rooftop display name — substituted as `brandName` in the template and used for the print filename. */
  rooftopName?: string;
  /** Show the QR/Text mode toggle and Print/QR UI. Defaults to true. */
  showQrMode?: boolean;
  /** Optional initial values when prefilling from a known guest. */
  defaultCustomerName?: string;
  defaultCustomerPhone?: string;
  /** Optional modal title override. */
  title?: string;
}

// HELPERS ---------------------------------------------------------------- //

const PHONE_DIGITS_RE = /\D+/g;

const normalizePhone = (raw: string): string => {
  const digits = raw.replace(PHONE_DIGITS_RE, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return raw.trim();
};

const isLikelyValidPhone = (raw: string): boolean => {
  const digits = raw.replace(PHONE_DIGITS_RE, '');
  return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
};

/** Pretty-format a raw 10-digit string as (555) 123-4567; pass anything else through unchanged. */
const formatPhoneForDisplay = (raw: string): string => {
  const digits = raw.replace(PHONE_DIGITS_RE, '');
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return raw;
};

/**
 * Lightweight Mustache renderer for the SMS template language.
 * Handles both `{{var}}` (HTML-escaped in Mustache, identity here since SMS is plain text)
 * and `{{& var}}` (explicitly unescaped). Unknown variables collapse to empty string.
 */
const renderTemplate = (template: string, vars: Record<string, string>): string => {
  return template.replace(/\{\{\s*&?\s*(\w+)\s*\}\}/g, (_match, key: string) => vars[key] ?? '');
};

const buildTemplateVars = (
  customerName: string,
  rooftopName: string | undefined,
  url: string,
): Record<string, string> => ({
  consumer: customerName.trim(),
  brandName: rooftopName ?? '',
  site: url,
});

// COMPONENT ---------------------------------------------------------------- //

const GuestLinkModal: React.FC<GuestLinkModalProps> = ({
  open,
  onClose,
  entityId,
  entityType = 'customer',
  templateType,
  rooftopName,
  showQrMode = true,
  defaultCustomerName,
  defaultCustomerPhone,
  title = 'Share Ride Request',
}) => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const user = useUser();

  // SMS is only offered for per-ride guest-ride links — not the rooftop-wide QR
  // lobby link (entityType 'customer'). A lobby link has no ride to attach the
  // message to, so we surface only the QR / copy options there.
  const smsEnabled = entityType === 'guestride';

  const [mode, setMode] = React.useState<ShareMode>(smsEnabled && !showQrMode ? 'sms' : 'qr');
  const [loading, setLoading] = React.useState(false);
  const [url, setUrl] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const [customerName, setCustomerName] = React.useState('');
  const [customerPhone, setCustomerPhone] = React.useState('');

  const [insertSmsMessage, { loading: sendingSms }] = useInsertGuestRideSmsMessageMutation({ errorPolicy: 'all' });

  const [fetchTemplate, { data: templateData, loading: templateLoading }] = useGetSmsTemplateByTypeLazyQuery({
    fetchPolicy: 'cache-first',
  });
  const smsTemplate = templateData?.smstemplates?.[0] ?? null;
  const template = smsTemplate?.template ?? null;

  const printRef = React.useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `guest-ride-qr-${rooftopName || 'rooftop'}`,
  });

  React.useEffect(() => {
    if (!open) return;

    setMode(entityType === 'guestride' && !showQrMode ? 'sms' : 'qr');
    setCustomerName(defaultCustomerName ?? '');
    setCustomerPhone(defaultCustomerPhone ? formatPhoneForDisplay(defaultCustomerPhone) : '');

    fetchTemplate({ variables: { type: templateType } });

    if (!entityId) {
      setError('Missing entity id for guest link');
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setUrl(null);

    fetchGuestLink(entityId, entityType)
      .then(result => {
        if (cancelled) return;
        if (result) {
          setUrl(result);
        } else {
          setError('Failed to generate guest link');
          toast.error("Couldn't generate the link");
        }
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to generate guest link:', err);
        Sentry.captureException(err, {
          tags: { feature: 'guest-rides', action: 'generate-link' },
          extra: { entityId, entityType, templateType },
        });
        setError('Failed to generate guest link');
        toast.error("Couldn't generate the link");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, entityId, entityType, templateType, showQrMode, defaultCustomerName, defaultCustomerPhone, fetchTemplate]);

  const handleCopy = () => {
    if (!url) return;
    copyToClipboard(url);
  };

  const phoneValid = isLikelyValidPhone(customerPhone);
  const nameValid = customerName.trim().length > 0;

  const canSendSms =
    !!url && !!template && !loading && !templateLoading && !sendingSms && phoneValid && nameValid;

  const handleSendSms = async () => {
    if (!url) return;
    if (!template) {
      toast.error('SMS template not loaded');
      return;
    }
    if (!nameValid) {
      toast.error('Enter a customer name');
      return;
    }
    if (!phoneValid) {
      toast.error('Enter a valid phone number');
      return;
    }

    const recipient = normalizePhone(customerPhone);

    // Append the recipient name + phone as query params so the guest rides site can
    // pre-fill the form fields. Falls back to the raw url if parsing fails.
    let linkWithParams = url;
    try {
      const u = new URL(url);
      u.searchParams.set('phone', recipient);
      const trimmedName = customerName.trim();
      if (trimmedName) u.searchParams.set('name', trimmedName);
      linkWithParams = u.toString();
    } catch {
      // leave linkWithParams as the original url
    }

    const messageBody = renderTemplate(template, buildTemplateVars(customerName, rooftopName, linkWithParams));

    try {
      const result = await insertSmsMessage({
        variables: {
          // Only a per-ride link carries a guestride id; rooftop-wide links have no ride yet.
          guestride_id: entityType === 'guestride' ? entityId : undefined,
          recipient_phone: recipient,
          sender_phone: config.twilioNumber,
          message_body: messageBody,
          direction: 'outbound',
          status: 'initiating',
          createdby: user?.profile?.display_name || user?.profile?.email || 'dealer-portal',
        },
      });

      if (result.errors?.length) {
        throw new Error(result.errors[0]?.message || 'Failed to send SMS');
      }

      toast.success(`SMS sent to ${customerName.trim() || recipient}`);
      setCustomerName('');
      setCustomerPhone('');
      onClose();
    } catch (err) {
      console.error('Failed to send guest link SMS:', err);
      Sentry.captureException(err, {
        tags: { feature: 'guest-rides', action: 'send-sms' },
        extra: { entityId, entityType, templateType },
      });
      toast.error('Failed to send SMS. Please try again.');
    }
  };

  return (
    <Modal open={open} handleClose={onClose} width='sm'>
      <ModalHeader handleClose={onClose}>{title}</ModalHeader>

      <ModalContent>
        {/* ── Mode toggle (only when QR mode is enabled AND SMS is offered) ─── */}
        {showQrMode && smsEnabled && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(_, next) => next && setMode(next)}
              size='small'
              color='primary'
            >
              <ToggleButton value='qr' sx={{ px: 3, textTransform: 'none' }}>
                <BiQr style={{ marginRight: 8 }} />
                QR Code
              </ToggleButton>
              <ToggleButton value='sms' sx={{ px: 3, textTransform: 'none' }}>
                <BiMessageRoundedDetail style={{ marginRight: 8 }} />
                Text Customer
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {/* ── QR mode ────────────────────────────────────────────── */}
        {showQrMode && mode === 'qr' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
            <div className={cls.qrSurface}>
              {loading && <CircularProgress size={32} />}

              {!loading && error && (
                <Typography variant='body2' color='error' sx={{ textAlign: 'center', px: 2 }}>
                  {error}
                </Typography>
              )}

              {!loading && !error && url && (
                <div ref={printRef} className={cls.printable}>
                  <Typography variant='body2' className={cls.printSubtitle}>
                    Scan to request a ride
                  </Typography>
                  <div className={cls.qrWrapper}>
                    <QRCodeSVG value={url} size={280} level='M' includeMargin />
                  </div>
                </div>
              )}
            </div>

            {url && !loading && !error && (
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ wordBreak: 'break-all', textAlign: 'center', px: 2 }}
              >
                {url}
              </Typography>
            )}
          </Box>
        )}

        {/* ── SMS mode ───────────────────────────────────────────── */}
        {smsEnabled && mode === 'sms' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 420, mx: 'auto' }}>
            <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center' }}>
              Send the ride request link directly to a customer's phone.
            </Typography>

            <TextField
              label='Customer name'
              value={customerName}
              onChange={e => setCustomerName(e.target.value)}
              size='small'
              fullWidth
              required
              disabled={!url || loading}
            />
            <TextField
              label='Customer phone'
              value={customerPhone}
              onChange={e => setCustomerPhone(e.target.value)}
              placeholder='(555) 123-4567'
              size='small'
              fullWidth
              required
              disabled={!url || loading}
              error={customerPhone.length > 0 && !phoneValid}
              helperText={
                customerPhone.length > 0 && !phoneValid
                  ? 'Enter a 10-digit US phone number'
                  : ' '
              }
            />
          </Box>
        )}
      </ModalContent>

      <ModalFooter>
        <Button variant='text' color='utility' onClick={onClose}>
          Close
        </Button>

        {/* Copy is always available when there's a URL */}
        <Button
          variant='outlined'
          color='primary'
          startIcon={<BiCopy />}
          onClick={handleCopy}
          disabled={!url || loading}
        >
          Copy Link
        </Button>

        {showQrMode && mode === 'qr' && (
          <Button
            variant='contained'
            color='primary'
            startIcon={<BiPrinter />}
            onClick={() => handlePrint()}
            disabled={!url || loading}
          >
            Print
          </Button>
        )}

        {smsEnabled && mode === 'sms' && (
          <Button
            variant='contained'
            color='primary'
            startIcon={<BiMessageRoundedDetail />}
            onClick={handleSendSms}
            disabled={!canSendSms}
          >
            {sendingSms ? 'Sending...' : 'Send SMS'}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme) => ({
  qrSurface: css`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 360px;
    padding: ${theme?.spacing(2)};
    background-color: ${theme?.palette?.background?.default};
    border-radius: 12px;
  `,
  printable: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 20px;
    background-color: #ffffff;
    border-radius: 8px;
  `,
  printTitle: css`
    font-weight: 600;
    text-align: center;
    color: ${theme?.palette?.text?.primary};
  `,
  printSubtitle: css`
    color: ${theme?.palette?.text?.secondary};
    text-align: center;
    margin-bottom: 4px;
  `,
  qrWrapper: css`
    background-color: #ffffff;
  `,
  previewBox: css`
    padding: 12px;
    background-color: ${theme?.palette?.background?.default};
    border-radius: 8px;
    border: 1px solid ${theme?.palette?.divider};
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default GuestLinkModal;
